import { api, extractApiErrorMessage } from '../api/client';
import { ROUTES } from '../api/routes';

type ApprovalHandoffResponse = {
  handoff: string;
  expiraEm: string;
};

type ConsumeApprovalHandoffResponse = {
  ok: true;
  solicitacaoId: string;
};

type ApprovalApiRequestOptions = {
  bearerToken?: string;
};

export type ApprovalStatusResponse = {
  id: string;
  tipo: string;
  status: 'PENDENTE' | 'AUTORIZADO' | 'NEGADO' | 'BLOQUEADO' | 'EXPIRADO' | 'CANCELADO';
  contexto: unknown;
  expiraEm: string;
  criadoEm: string;
  dezenaCorreta?: string | null;
  dezenasOpcoes?: string | null;
  tipoBloqueio?: string | null;
};

export type RespondApprovalInput = {
  solicitacaoId: string;
  resposta: 'AUTORIZADO' | 'NEGADO' | 'BLOQUEADO';
  tipoBloqueio?: string;
  opcao?: 'TEMPORARIO' | 'PERMANENTE' | 'PASSKEY';
  respostaDezena?: string;
};

export type RegisterApprovalDeviceInput = {
  fingerprint: string;
  nome?: string;
  tipoLocal?: string | null;
  somenteMeu?: boolean | null;
  navegador?: string;
  sistemaOp?: string;
};

export type RequestSuperiorApprovalInput = {
  contexto?: Record<string, unknown>;
};

export type SuperiorApprovalResponse = {
  id: string;
  [key: string]: unknown;
};

export type VerifyApprovalEmailInput = {
  codigo: string;
  fingerprint?: string;
};

export type ApprovalPreferencesResponse = {
  timeoutBloqueioMinutos?: number;
  timeoutDesconectarMinutos?: number;
  [key: string]: unknown;
};

function authSurfaceConfig(options?: ApprovalApiRequestOptions) {
  const config: {
    headers: { 'X-AC-Use-Bearer': 'true' };
    _acBearerToken?: string;
  } = {
    headers: {
      'X-AC-Use-Bearer': 'true',
    },
  };
  const bearerToken = options?.bearerToken?.trim();
  if (bearerToken) {
    config._acBearerToken = bearerToken;
  }
  return config;
}

export async function createApprovalHandoff(
  solicitacaoId: string,
): Promise<ApprovalHandoffResponse> {
  try {
    const { data } = await api.post<ApprovalHandoffResponse>(
      ROUTES.security.approval.handoff(solicitacaoId),
      {},
      authSurfaceConfig(),
    );

    if (!data?.handoff) {
      throw new Error('Handoff nao retornado pelo servidor.');
    }

    return data;
  } catch (err) {
    if (err instanceof Error && err.message.includes('Handoff nao retornado')) {
      throw err;
    }
    throw new Error(extractApiErrorMessage(err, 'Falha ao gerar QR seguro.'));
  }
}

export async function consumeApprovalHandoff(
  solicitacaoId: string,
  handoff: string,
): Promise<ConsumeApprovalHandoffResponse> {
  try {
    const { data } = await api.post<ConsumeApprovalHandoffResponse>(
      ROUTES.security.approval.handoffConsume(solicitacaoId),
      { handoff },
      authSurfaceConfig(),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Handoff invalido ou expirado.'));
  }
}

export async function getApprovalStatus(
  solicitacaoId: string,
  options?: ApprovalApiRequestOptions,
): Promise<ApprovalStatusResponse> {
  try {
    const { data } = await api.get<ApprovalStatusResponse>(
      ROUTES.security.approval.status(solicitacaoId),
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Solicitacao nao encontrada.'));
  }
}

export async function respondApproval(
  input: RespondApprovalInput,
  options?: ApprovalApiRequestOptions,
): Promise<unknown> {
  try {
    const { data } = await api.post(
      ROUTES.security.approval.responder,
      input,
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao enviar resposta de autorizacao.'));
  }
}

export async function registerApprovalDevice(
  input: RegisterApprovalDeviceInput,
  options?: ApprovalApiRequestOptions,
): Promise<unknown> {
  try {
    const { data } = await api.post(
      ROUTES.security.approval.deviceRegister,
      input,
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao registrar dispositivo.'));
  }
}

export async function verifyApprovalSession(
  options?: ApprovalApiRequestOptions,
): Promise<unknown> {
  try {
    const { data } = await api.get(ROUTES.auth.me, authSurfaceConfig(options));
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Sessao invalida ou expirada.'));
  }
}

export async function requestApprovalEmailCode(
  options?: ApprovalApiRequestOptions,
): Promise<unknown> {
  try {
    const { data } = await api.post(
      ROUTES.security.approval.solicitarEmail,
      {},
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Erro ao enviar codigo por e-mail.'));
  }
}

export async function verifyApprovalEmailCode(
  input: VerifyApprovalEmailInput,
  options?: ApprovalApiRequestOptions,
): Promise<unknown> {
  try {
    const { data } = await api.post(
      ROUTES.security.approval.verificarEmail,
      input,
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Codigo incorreto ou expirado.'));
  }
}

export async function requestSuperiorApproval(
  input: RequestSuperiorApprovalInput,
  options?: ApprovalApiRequestOptions,
): Promise<SuperiorApprovalResponse> {
  try {
    const { data } = await api.post<SuperiorApprovalResponse>(
      ROUTES.security.approval.solicitarSuperior,
      input,
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Erro ao solicitar aprovacao ao superior.'));
  }
}

export async function respondTestApproval(
  solicitacaoId: string,
  resposta: 'AUTORIZADO' | 'NEGADO',
): Promise<unknown> {
  try {
    const { data } = await api.post(
      ROUTES.security.approval.responderTeste,
      { solicitacaoId, resposta },
      { headers: { 'X-AC-Use-Bearer': 'false' } },
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao registrar aprovacao de teste.'));
  }
}

export async function listPendingApprovals(
  options?: ApprovalApiRequestOptions,
): Promise<ApprovalStatusResponse[]> {
  try {
    const { data } = await api.get<ApprovalStatusResponse[]>(
      ROUTES.security.approval.pendentes,
      authSurfaceConfig(options),
    );
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao buscar aprovacoes pendentes.'));
  }
}

export async function getApprovalPreferences(
  options?: ApprovalApiRequestOptions,
): Promise<ApprovalPreferencesResponse | null> {
  try {
    const { data } = await api.get<ApprovalPreferencesResponse>(
      ROUTES.security.approval.preferencias,
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao carregar preferencias de seguranca.'));
  }
}
